/**
 * Query helpers and response parsing for
 * `GET /inventory/objects/{groupID}/passport/table-bound-passports` (see inventory `GetTableBoundPassports`).
 */
import { formatLocalWallClockVerDateNow } from '@/shared/lib'

export type TableBoundPassportsSearchOpts = {
  objectId: string
  tableFieldId: string
  statusDate: string | null
  verDate?: string | null
  listGroupId?: number | null
}

/**
 * Builds URLSearchParams matching server precedence: `status_date` when non-empty after trim,
 * else `ver_date` when non-empty; always `object_id`, `table_field_id`; optional `list_group_id` when positive.
 */
export function buildTableBoundPassportsSearchParams(opts: TableBoundPassportsSearchOpts): URLSearchParams {
  const params = new URLSearchParams()
  params.set('object_id', opts.objectId)
  params.set('table_field_id', opts.tableFieldId.trim())

  const status = (opts.statusDate ?? '').trim()
  if (status !== '') {
    params.set('status_date', status)
  } else {
    const ver = (opts.verDate ?? '').trim()
    if (ver !== '') {
      params.set('ver_date', ver)
    } else {
      params.set('client_now', formatLocalWallClockVerDateNow())
    }
  }

  const lg = opts.listGroupId
  if (lg != null && lg > 0) {
    params.set('list_group_id', String(lg))
  }

  return params
}

/**
 * Parses bulk table-bound response body: `{ data: [...] }` or a JSON array root.
 * Malformed payloads yield `{ data: [] }`.
 */
export function parseTableBoundPassportsResponse(res: unknown): { data: unknown[] } {
  if (Array.isArray(res)) {
    return { data: res }
  }
  if (res == null || typeof res !== 'object') {
    return { data: [] }
  }
  const root = res as Record<string, unknown>
  const raw = root.data
  if (Array.isArray(raw)) {
    return { data: raw }
  }
  return { data: [] }
}
